import { useTranslation } from 'react-i18next';

import { OrderSettingsApi } from '@/api/domains/order-settings.api';
import { RHFFieldsMatchingOptionsField, useFieldMatchingOptions } from '@/components/FieldMatching';
import {
  RECONCILIATION_ALL_FIELDS,
  RECONCILIATION_COLUMN_MAKER,
  RECONCILIATION_COLUMN_NAME,
  RECONCILIATION_COLUMN_NAME_AND_MAKER,
  RECONCILIATION_REQUIRED_FIELDS,
} from '@/pages/orders/reconciliation/reconciliation.constants';

import { useReconciliationDocumentType } from '../../reconciliation-file.store';

type ReconciliationMatchingOptionsProps = {
  columnNumber: number;
};

export const ReconciliationMatchingOptions = (props: ReconciliationMatchingOptionsProps) => {
  const { columnNumber } = props;
  const { t } = useTranslation('orders-page');
  const documentType = useReconciliationDocumentType();

  const getTitle = (key: string) => t(`reconciliation.field-matching.columns.${key}`);

  const { query, titleByKey, selectedOptions, options } = useFieldMatchingOptions({
    queryKey: ['reconciliation-columns', documentType],
    fetchFn: async () => {
      try {
        const { columns } = await OrderSettingsApi.getColumns(documentType);

        return {
          items: columns.map(({ key }) => ({
            key,
            title: getTitle(key),
          })),
        };
      } catch {
        return {
          items: RECONCILIATION_ALL_FIELDS.map(key => ({
            key,
            title: getTitle(key),
          })),
        };
      }
    },
  });

  const hasNameOrMaker =
    selectedOptions.includes(RECONCILIATION_COLUMN_NAME) || selectedOptions.includes(RECONCILIATION_COLUMN_MAKER);
  const hasNameAndMaker = selectedOptions.includes(RECONCILIATION_COLUMN_NAME_AND_MAKER);

  const requiredOptions = hasNameAndMaker
    ? [
        ...RECONCILIATION_REQUIRED_FIELDS.filter(
          f => f !== RECONCILIATION_COLUMN_NAME && f !== RECONCILIATION_COLUMN_MAKER,
        ),
        RECONCILIATION_COLUMN_NAME_AND_MAKER,
      ]
    : hasNameOrMaker
      ? [...RECONCILIATION_REQUIRED_FIELDS]
      : [...RECONCILIATION_REQUIRED_FIELDS, RECONCILIATION_COLUMN_NAME_AND_MAKER];

  return (
    <RHFFieldsMatchingOptionsField
      options={options}
      isInitialLoading={query.isInitialLoading}
      titleByKey={titleByKey}
      selectedOptions={selectedOptions}
      name={`columns.column-${columnNumber}`}
      getOptionDisabled={option => {
        if (!option) return false;
        if (selectedOptions.includes(option)) return true;

        if (option === RECONCILIATION_COLUMN_NAME_AND_MAKER && hasNameOrMaker) return true;
        if ((option === RECONCILIATION_COLUMN_NAME || option === RECONCILIATION_COLUMN_MAKER) && hasNameAndMaker)
          return true;

        return false;
      }}
      requiredOptions={requiredOptions}
    />
  );
};
